<template>
  <section class="education-block">
    <div class="education-head education-head-sticky">
      <div class="education-head-top">
        <h3 class="education-title">Education</h3>
        <button type="button" class="btn-add btn-add-primary btn-add-bold" @click="add"><strong>+ Add</strong></button>
      </div>
      <p class="education-helper">Degrees and certifications, most recent first.</p>
    </div>
    <div
      v-for="(edu, index) in model"
      :key="index"
      class="education-card"
      :ref="(el) => setCardRef(index, el)"
    >
      <div class="education-card-head">
        <span></span>
        <button type="button" class="btn-remove-link" @click="confirmRemove(index)" aria-label="Remove this education">
          <i class="fa fa-trash-alt" aria-hidden="true"></i> Remove
        </button>
      </div>
      <EducationItemEditor
        :ref="(el) => setItemRef(index, el)"
        :model-value="model[index]"
        @update:model-value="onEduUpdate(index, $event)"
      />
    </div>
    <p v-if="!model.length" class="education-empty">No education entries yet. Tap <strong>&quot;+ Add&quot;</strong> to get started.</p>

    <!-- Confirmation modal -->
    <div v-if="pendingRemoveIndex !== null" class="remove-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="remove-edu-modal-title">
      <div class="remove-modal">
        <h4 id="remove-edu-modal-title">Remove this education?</h4>
        <p>This will delete this education entry.</p>
        <div class="remove-modal-actions">
          <button type="button" class="wizard-btn wizard-btn-secondary" @click="pendingRemoveIndex = null">Cancel</button>
          <button type="button" class="wizard-btn wizard-btn-danger" @click="commitRemove">Remove</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import EducationItemEditor from '@/components/EducationItemEditor.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] }
});
const emit = defineEmits(['update:modelValue']);

const pendingRemoveIndex = ref(null);
const cardRefs = ref({});
const itemRefs = ref({});

function setCardRef(index, el) {
  if (el) cardRefs.value[index] = el;
}

function setItemRef(index, el) {
  if (el) itemRefs.value[index] = el;
}

const model = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
});

async function add() {
  const newIndex = props.modelValue.length;
  emit('update:modelValue', [
    ...props.modelValue,
    { degree: '', institution: '', year: '', startYear: null, endYear: null }
  ]);
  await nextTick();
  const cardEl = cardRefs.value[newIndex];
  const itemComp = itemRefs.value[newIndex];
  if (cardEl?.scrollIntoView) {
    cardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  if (itemComp?.focusFirstInput) {
    setTimeout(() => itemComp.focusFirstInput(), 300);
  }
}

function onEduUpdate(index, val) {
  const next = [...props.modelValue];
  next[index] = val;
  emit('update:modelValue', next);
}

function confirmRemove(index) {
  pendingRemoveIndex.value = index;
}

function commitRemove() {
  if (pendingRemoveIndex.value === null) return;
  const next = props.modelValue.filter((_, i) => i !== pendingRemoveIndex.value);
  emit('update:modelValue', next);
  pendingRemoveIndex.value = null;
}
</script>

<style scoped>
.education-block {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.education-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}
.education-head-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.education-head-sticky {
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--app-surface);
  padding: 0 0 12px;
  margin: 0 0 8px;
  border-bottom: 1px solid var(--app-border);
}
.education-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--app-text);
}
.education-helper {
  margin: 0;
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.45;
}
.btn-add {
  padding: 8px 14px;
  min-width: 72px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: center;
}
.btn-add-primary {
  background: var(--app-primary);
  color: var(--app-primary-text);
}
.btn-add-primary:hover {
  background: var(--app-primary-hover);
}
.btn-add-bold strong {
  font-weight: 700;
}
.education-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 24px;
  position: relative;
}
.education-card-head {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: -8px -8px 16px 0;
}
.btn-remove-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--app-error);
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}
.btn-remove-link:hover {
  color: #991b1b;
  background: var(--danger-subtle);
}
.btn-remove-link i {
  font-size: 12px;
}
.education-empty {
  color: var(--text-muted);
  font-size: 14px;
  margin: 0;
}

/* Confirmation modal */
.remove-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--app-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.remove-modal {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 24px;
  max-width: 360px;
  width: 100%;
  box-shadow: var(--shadow-modal);
}
.remove-modal h4 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: var(--text-primary);
}
.remove-modal p {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--text-secondary);
}
.remove-modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
.wizard-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  transition: background 0.2s, color 0.2s;
}
.wizard-btn-secondary {
  background: var(--surface-2);
  color: var(--text-primary);
  border: 1px solid var(--border);
}
.wizard-btn-secondary:hover {
  background: var(--surface-3);
}
.wizard-btn-danger {
  background: var(--danger);
  color: var(--danger-foreground);
}
.wizard-btn-danger:hover {
  filter: brightness(1.05);
}

@media (max-width: 768px) {
  .education-block {
    gap: 14px;
  }
  /* Sticky head clips scrolled fields on mobile */
  .education-head-sticky {
    position: static;
    padding-bottom: 0;
    margin-bottom: 4px;
    border-bottom: none;
  }
  .education-title {
    font-size: 1.05rem;
  }
  .education-helper {
    font-size: 13px;
  }
  .btn-add {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 68px;
  }
  .education-card {
    padding: 12px;
  }
  .education-card-head {
    margin: 0 0 8px 0;
  }
  .education-empty {
    font-size: 13px;
  }
}
</style>
